import { Button, Card, Empty, Flex, Form, Input, Select, Space, Spin, Tooltip } from "antd"
import { useWatch } from "antd/es/form/Form"
import { PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import { paypalLogoUrl } from "helpers/constant"
import { numberToCurrency } from "helpers/string"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import TipSection from "ui/TipSection"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import AddCardModal from "pagesV2/pro/Bookings/BookingForm/AddCardModal"
import stripeIcon from "assets/imagesV2/settings/stripe.svg"
import { getFormattedBookingDate } from "ui/Booking/Details/utils"
import { Avatar } from "ui/Avatar"
import { getMediaUrl } from "utils/media-upload"
import { grossUpCardFee, hasCreationBakedFees, roundToCents } from "utils/bookingCalculations"
import { LINK_PROVIDERS, OTHERS_TYPES, PAYMENT_TABS } from "./constants"
import { useTakePaymentForm } from "./useTakePaymentForm"
import styles from "./index.module.css"

const TakePaymentForm = ({
  remainingBalance,
  bookingUUID,
  bookingId,
  onClose,
  bookingTitle,
  bookedOn,
  clientName,
  clientEmail,
  experienceAvatar,
  paymentOption,
  adjustmentOutstandingBase,
  bookingStatus
}) => {
  const [form] = Form.useForm()
  const [modalForm] = Form.useForm()
  const { t } = useTranslation()

  const {
    activeTab,
    setActiveTab,
    linkProvider,
    setLinkProvider,
    tipAmount,
    setTipAmount,
    selectedCardId,
    setSelectedCardId,
    isModalVisible,
    setIsModalVisible,
    isAddingCard,
    isSubmitting,
    isStripeConnected,
    isPaypalConnected,
    isStripeChargeable,
    stripeProblemMessage,
    paymentMethods,
    paymentMethodsLoading,
    clientId,
    visibleTabs,
    linkInfoText,
    amountLabel,
    submitLabel,
    handleSubmit,
    createPaymentMethod,
    feeSummaryData
  } = useTakePaymentForm({ remainingBalance, bookingUUID, bookingId, onClose, clientEmail, form, modalForm })

  const amountValue = useWatch("amount", form)
  const baseAmount = Number(amountValue) || 0

  // When there is no outstanding balance, the booking amount can't be paid — only a tip can be collected.
  const isZeroBalance = Number(remainingBalance) <= 0

  // Fees were already captured during the initial booking flow for requested bookings,
  // so no additional platform or card processing fees should be charged when collecting.
  const isRequestedBooking = bookingStatus?.toLowerCase() === "booking request"

  // outstanding_base is the remaining price-increase amount that still owes a fee (0 when the
  // adjustment is fully settled / there was no increase).
  const outstandingBase = Number(adjustmentOutstandingBase) || 0
  const hasOutstandingAdjustment = outstandingBase > 0

  const isCardOrLink = activeTab === PAYMENT_TABS.CARD || activeTab === PAYMENT_TABS.LINK
  // The platform fee is only captured through Stripe (card tab, or link tab with the Stripe provider),
  // never PayPal / cash / other.
  const isStripeCardOrLink = activeTab === PAYMENT_TABS.CARD || (activeTab === PAYMENT_TABS.LINK && linkProvider === LINK_PROVIDERS.STRIPE)

  // No card processing fee is charged on the remaining balance for deposit + balance later bookings,
  // or for requested bookings (fees already collected at booking time).
  const skipCardFee = isRequestedBooking || paymentOption === "deposit_and_balance_later" || paymentOption === "deposit_by_link_and_balance_later"
  // Platform fee (guest portion) is collected on full-payment options — full amount today, full
  // amount in cash on the day, and full amount by link. It's charged as amount_charged_to_guest
  // (e.g. 4.5%) on the amount being collected, so a partial collection is fee'd on that amount only.
  // Never applied to requested bookings since fees were already taken at booking time.
  const collectPlatformFee =
    !isRequestedBooking &&
    (paymentOption === "full_amount_in_cash_on_day" ||
      paymentOption === "full_amount_today" ||
      paymentOption === "full_amount_by_link")

  // The amount the platform + card fees are charged on. For the options that bake their fees at
  // booking creation (full_amount_today and full_amount_by_link) the balance ALREADY contains them,
  // so only a later price increase (outstanding_base, capped at what's actually being collected)
  // still owes a fee — fee'ing the balance again quotes the guide a total the API will never charge.
  // full_amount_in_cash_on_day bakes nothing, so it fees the full amount being collected.
  const feeableAmount = hasCreationBakedFees(paymentOption) ? Math.min(outstandingBase, baseAmount) : baseAmount

  // Guest-portion platform-fee rate (e.g. 4.5% = the guest's share of the 5% base). This is the rate
  // used for the platform fee on EVERY path — full-payment collection and the outstanding
  // price-increase adjustment alike — matching how the Adjust Price modal charges it.
  const platformFeeConfig = feeSummaryData?.platform_fee
  const guestPlatformRate =
    platformFeeConfig?.charged_to_guest && platformFeeConfig?.amount_charged_to_guest?.type === "percentage"
      ? Number(platformFeeConfig.amount_charged_to_guest.value) || 0
      : 0

  // Platform fee — guest portion, applies to card and link tabs. Charged on the feeable amount
  // (see above), not the full booking subtotal.
  const platformFee =
    collectPlatformFee && isCardOrLink && guestPlatformRate > 0 && feeableAmount > 0
      ? roundToCents((feeableAmount * guestPlatformRate) / 100)
      : 0

  // Adjustment platform fee — the same guest-portion platform fee, owed on a past upward price
  // adjustment of a cash-balance booking (deposit + balance-in-cash options). Only captured when the
  // guide collects by Stripe card/link; cash/PayPal/other add no fee. Charged at the guest rate on
  // the outstanding price increase, capped at what's actually being collected. Gated on
  // outstanding_base — when the adjustment is fully settled (0) nothing is collected. Display-only;
  // the backend adds it authoritatively (see useTakePaymentForm).
  const adjustmentFee =
    !isRequestedBooking && !collectPlatformFee && isStripeCardOrLink && hasOutstandingAdjustment && guestPlatformRate > 0
      ? roundToCents((Math.min(outstandingBase, baseAmount) * guestPlatformRate) / 100)
      : 0

  // Card processing fee — gross-up applied to (base + platform fee) so that after Stripe's
  // deduction the net received equals the entered amount plus platform fee collection.
  // Card tab: use selected card's rate/fixed (Stripe provides per-card fees).
  // Link tab: use fee_summary percentage (2.9%) with standard Stripe fixed fee ($0.30).
  const cardFeeConfig = feeSummaryData?.card_processing_fee
  const cardFeePassthrough = cardFeeConfig?.passthrough_enabled && cardFeeConfig?.charged_to_guest

  const selectedCard = paymentMethods?.find((pm) => pm?.payment_method_id === selectedCardId)
  const { rate: cardRate = 0.029, fixed: cardFixed = 0.3 } = selectedCard?.card_fee_info ?? {}

  // Card processing fee is grossed up on the feeable amount (see above) plus the fees added on it, so
  // the guide nets that amount after Stripe's cut. For the creation-baked options this is the price
  // increase only (the rest already had a card fee baked into the price); others use the full amount.
  const grossUpBase = feeableAmount + platformFee + adjustmentFee
  const cardProcessingFee = (() => {
    if (skipCardFee || !isCardOrLink || !cardFeePassthrough || grossUpBase <= 0) return 0
    if (activeTab === PAYMENT_TABS.CARD) {
      return grossUpCardFee(grossUpBase, { rate: Number(cardRate), fixed: Number(cardFixed) })
    }
    // Link: use fee_summary rate, standard Stripe fixed fee
    const linkRate = (cardFeeConfig?.percentage ?? 2.9) / 100
    return grossUpCardFee(grossUpBase, { rate: linkRate, fixed: 0.3 })
  })()

  const totalToPay = baseAmount + platformFee + adjustmentFee + tipAmount + cardProcessingFee

  // Informational: outstanding adjustment platform fee (guest rate on the price increase) that
  // cash/PayPal/other won't capture.
  const outstandingPlatformFee = roundToCents((outstandingBase * guestPlatformRate) / 100)
  const showCashAdjustmentHint = !collectPlatformFee && !isStripeCardOrLink && hasOutstandingAdjustment && outstandingPlatformFee > 0

  return (
    <Spin spinning={isSubmitting} tip="Processing payment...">
      <Space direction="vertical" size={24} className="w-full">
        <div className={styles.formContent}>
          <Space direction="vertical" size={24} className="w-full">
            {(bookingTitle || clientName || clientEmail) && (
              <Flex gap={12} align="center" className={styles.bookingInfo}>
                {getMediaUrl(experienceAvatar) && (
                  <Avatar fullName={bookingTitle} src={getMediaUrl(experienceAvatar)} shape="square" size={64} />
                )}
                <Flex vertical gap={4}>
                  {bookingTitle && (
                    <Text fw={500}>
                      {bookingTitle}
                      {bookedOn ? ` · ${getFormattedBookingDate(bookedOn)}` : ""}
                    </Text>
                  )}
                  {(clientName || clientEmail) && (
                    <Text color={colors.textSecondaryDark}>{[clientName, clientEmail].filter(Boolean).join(" · ")}</Text>
                  )}
                </Flex>
              </Flex>
            )}

            <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s">
              <Text color={colors.textSecondaryDark}>{t("client.payment.outstandingBalance")}</Text>
              <Text color={colors.textPrimary} fw={500}>
                {numberToCurrency(remainingBalance)}
              </Text>
            </Flex>

            <div>
              <Text className={styles.sectionLabel}>CHOOSE PAYMENT METHOD</Text>
              <div className={styles.tabContainer}>
                {visibleTabs.map((tab) => (
                  <Tooltip key={tab.key} title={tab.disabled ? tab.tooltip : ""}>
                    <button
                      type="button"
                      className={`${activeTab === tab.key ? styles.tabButtonActive : styles.tabButton} ${tab.disabled ? styles.tabButtonDisabled : ""}`}
                      onClick={() => !tab.disabled && setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>

            <Form form={form} layout="vertical" initialValues={{ amount: remainingBalance }}>
              <Form.Item
                label={amountLabel}
                name="amount"
                rules={
                  isZeroBalance
                    ? []
                    : [
                        { required: true, message: "Amount is required" },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.reject()
                            const num = Number.parseFloat(value)
                            if (Number.isNaN(num) || num <= 0) return Promise.reject("Enter a valid amount")
                            if (num > remainingBalance) return Promise.reject(t("client.payment.amountToPayCannotExceedOutstandingBalance"))
                            return Promise.resolve()
                          }
                        }
                      ]
                }
              >
                <Input prefix="$" type="number" min="0.01" step="0.01" disabled={isZeroBalance} />
              </Form.Item>

              <div className={styles.tabContent}>
                {activeTab === PAYMENT_TABS.CARD && (
                  <Spin spinning={paymentMethodsLoading}>
                    {paymentMethods?.length ? (
                      <>
                        <div className={styles.cardListContainer}>
                          <PaymentMethodList
                            active={selectedCardId}
                            setActive={setSelectedCardId}
                            paymentMethods={paymentMethods}
                            clientId={clientId}
                          />
                        </div>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          style={{ color: colors.sportsmanBrand, fontWeight: 500, fontSize: 16, marginTop: 8 }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          {t("booking.labels.add_card")}
                        </Button>
                      </>
                    ) : (
                      !isAddingCard && (
                        <Card>
                          <Empty
                            image={<NoCardImage />}
                            description={
                              <>
                                <p>{t("booking.labels.no_card_title")}</p>
                                <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                  {t("booking.labels.add_card")}
                                </Button>
                              </>
                            }
                          />
                        </Card>
                      )
                    )}
                  </Spin>
                )}

                {activeTab === PAYMENT_TABS.LINK && (
                  <Space direction="vertical" className="w-full">
                    <Flex gap={8}>
                      {isStripeConnected && (
                        <Tooltip title={!isStripeChargeable ? stripeProblemMessage : ""}>
                          <button
                            type="button"
                            className={`${linkProvider === LINK_PROVIDERS.STRIPE ? styles.providerButtonActive : styles.providerButton} ${
                              !isStripeChargeable ? styles.providerButtonDisabled : ""
                            }`}
                            onClick={() => isStripeChargeable && setLinkProvider(LINK_PROVIDERS.STRIPE)}
                          >
                            <img src={stripeIcon} alt="Stripe" height={16} /> Stripe
                          </button>
                        </Tooltip>
                      )}
                      {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
                        <button
                          type="button"
                          className={linkProvider === LINK_PROVIDERS.PAYPAL ? styles.providerButtonActive : styles.providerButton}
                          onClick={() => setLinkProvider(LINK_PROVIDERS.PAYPAL)}
                        >
                          <img src={paypalLogoUrl} alt="PayPal" height={16} /> PayPal
                        </button>
                      )}
                    </Flex>
                    <div className={styles.linkInfo}>
                      <Text color={colors.textSecondaryDark}>{linkInfoText}</Text>
                    </div>
                  </Space>
                )}

                {activeTab === PAYMENT_TABS.CASH && (
                  <div className={styles.cashInfo}>
                    <Text color={colors.textSecondaryDark}>Record a cash payment received in person.</Text>
                  </div>
                )}

                {activeTab === PAYMENT_TABS.OTHERS && (
                  <Space direction="vertical" className="w-full">
                    <Form.Item name="othersType" label="Payment Type" rules={[{ required: true, message: "Please select a payment type" }]}>
                      <Select placeholder="Select payment type" options={OTHERS_TYPES} />
                    </Form.Item>
                    <Form.Item name="note" label="Reference / Note">
                      <Input.TextArea placeholder="Add a reference number or note (optional)" rows={3} />
                    </Form.Item>
                  </Space>
                )}
              </div>

              {showCashAdjustmentHint && (
                <div className={styles.cashInfo}>
                  <Text color={colors.textSecondaryDark}>
                    Collecting by card or Stripe link will also capture the {numberToCurrency(outstandingPlatformFee)} platform fee owed on
                    the price increase.
                  </Text>
                </div>
              )}

              {activeTab === PAYMENT_TABS.CARD && (
                <TipSection baseAmount={remainingBalance} onTipChange={setTipAmount} disablePresets={isZeroBalance} />
              )}
            </Form>
          </Space>
        </div>

        <Flex vertical gap={4} className={styles.totalRow}>
          <Flex justify="space-between">
            <Text color={colors.textSecondaryDark}>Booking Payment</Text>
            <Text color={colors.textSecondaryDark}>{numberToCurrency(baseAmount)}</Text>
          </Flex>
          {platformFee > 0 && (
            <Flex justify="space-between">
              <Text color={colors.textSecondaryDark}>{platformFeeConfig?.title ?? "Platform Fee"}</Text>
              <Text color={colors.textSecondaryDark}>+{numberToCurrency(platformFee)}</Text>
            </Flex>
          )}
          {adjustmentFee > 0 && (
            <Flex justify="space-between">
              <Text color={colors.textSecondaryDark}>{platformFeeConfig?.title ?? "Platform Fee"}</Text>
              <Text color={colors.textSecondaryDark}>+{numberToCurrency(adjustmentFee)}</Text>
            </Flex>
          )}
          {tipAmount > 0 && (
            <Flex justify="space-between">
              <Text color={colors.textSecondaryDark}>Tip</Text>
              <Text color={colors.textSecondaryDark}>+{numberToCurrency(tipAmount)}</Text>
            </Flex>
          )}
          {cardProcessingFee > 0 && (
            <Flex justify="space-between">
              <Text color={colors.textSecondaryDark}>{cardFeeConfig?.title ?? "Card Processing Fee"}</Text>
              <Text color={colors.textSecondaryDark}>+{numberToCurrency(cardProcessingFee)}</Text>
            </Flex>
          )}
          <Flex justify="space-between">
            <Text fw={700}>Total to Pay</Text>
            <Text fw={700}>{numberToCurrency(totalToPay)}</Text>
          </Flex>
        </Flex>

        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button
            type="primary"
            className="py-l px-xl"
            disabled={isZeroBalance && tipAmount <= 0}
            onClick={() => handleSubmit(amountValue)}
          >
            {submitLabel}
          </Button>
        </Flex>
      </Space>

      {isModalVisible && (
        <AddCardModal
          visible={isModalVisible}
          onCancel={() => {
            modalForm.resetFields()
            setIsModalVisible(false)
          }}
          onSubmit={createPaymentMethod}
          form={modalForm}
          loading={isAddingCard}
        />
      )}
    </Spin>
  )
}

TakePaymentForm.propTypes = {
  remainingBalance: PropTypes.number,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  bookingTitle: PropTypes.string,
  bookedOn: PropTypes.string,
  clientName: PropTypes.string,
  clientEmail: PropTypes.string,
  experienceAvatar: PropTypes.array,
  paymentOption: PropTypes.string,
  adjustmentOutstandingBase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingStatus: PropTypes.string
}

export default TakePaymentForm
